import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type ThemeColor = 'rose' | 'blue' | 'seagreen' | 'purple' | 'amber' | 'teal' | 'indigo';

export interface Theme {
  name: string;
  primary: string;
  primarySolid: string;
  primaryText: string;
  primaryHover: string;
  gradient: string;
  light: string;
  badge: string;
  border: string;
  icon: string;
  accent: string;
  primaryGradient: string; // CSS gradient for inline styles
}

interface ThemeContextType {
  theme: ThemeColor;
  setTheme: (theme: ThemeColor) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const themes: Record<ThemeColor, Theme> = {
  rose: {
    name: 'Rose Romance',
    primary: 'from-rose-700 to-pink-600',
    primarySolid: 'bg-rose-600',
    primaryText: 'text-rose-600',
    primaryHover: 'hover:bg-rose-700',
    gradient: 'bg-gradient-to-r from-rose-700 to-pink-600',
    light: 'bg-rose-50',
    badge: 'bg-rose-500',
    border: 'border-rose-100',
    icon: '#e11d48',
    accent: '#f43f5e',
    primaryGradient: 'linear-gradient(to right, #be123c, #db2777)'
  },
  blue: {
    name: 'Ocean Blue',
    primary: 'from-blue-700 to-cyan-600',
    primarySolid: 'bg-blue-600',
    primaryText: 'text-blue-600',
    primaryHover: 'hover:bg-blue-700',
    gradient: 'bg-gradient-to-r from-blue-700 to-cyan-600',
    light: 'bg-blue-50',
    badge: 'bg-blue-500',
    border: 'border-blue-100',
    icon: '#2563eb',
    accent: '#3b82f6',
    primaryGradient: 'linear-gradient(to right, #1d4ed8, #0891b2)'
  },
  seagreen: {
    name: 'Sea Green',
    primary: 'from-emerald-700 to-teal-600',
    primarySolid: 'bg-emerald-600',
    primaryText: 'text-emerald-600',
    primaryHover: 'hover:bg-emerald-700',
    gradient: 'bg-gradient-to-r from-emerald-700 to-teal-600',
    light: 'bg-emerald-50',
    badge: 'bg-emerald-500',
    border: 'border-emerald-100',
    icon: '#059669',
    accent: '#10b981',
    primaryGradient: 'linear-gradient(to right, #047857, #0d9488)'
  },
  purple: {
    name: 'Royal Purple',
    primary: 'from-purple-700 to-indigo-600',
    primarySolid: 'bg-purple-600',
    primaryText: 'text-purple-600',
    primaryHover: 'hover:bg-purple-700',
    gradient: 'bg-gradient-to-r from-purple-700 to-indigo-600',
    light: 'bg-purple-50',
    badge: 'bg-purple-500',
    border: 'border-purple-100',
    icon: '#9333ea',
    accent: '#a855f7',
    primaryGradient: 'linear-gradient(to right, #7e22ce, #4f46e5)'
  },
  amber: {
    name: 'Golden Amber',
    primary: 'from-amber-700 to-orange-600',
    primarySolid: 'bg-amber-600',
    primaryText: 'text-amber-600',
    primaryHover: 'hover:bg-amber-700',
    gradient: 'bg-gradient-to-r from-amber-700 to-orange-600',
    light: 'bg-amber-50',
    badge: 'bg-amber-500',
    border: 'border-amber-100',
    icon: '#d97706',
    accent: '#f59e0b',
    primaryGradient: 'linear-gradient(to right, #b45309, #ea580c)'
  },
  teal: {
    name: 'Teal Dream',
    primary: 'from-teal-700 to-cyan-600',
    primarySolid: 'bg-teal-600',
    primaryText: 'text-teal-600',
    primaryHover: 'hover:bg-teal-700',
    gradient: 'bg-gradient-to-r from-teal-700 to-cyan-600',
    light: 'bg-teal-50',
    badge: 'bg-teal-500',
    border: 'border-teal-100',
    icon: '#0d9488',
    accent: '#14b8a6',
    primaryGradient: 'linear-gradient(to right, #0f766e, #0891b2)'
  },
  indigo: {
    name: 'Indigo Night',
    primary: 'from-indigo-700 to-blue-600',
    primarySolid: 'bg-indigo-600',
    primaryText: 'text-indigo-600',
    primaryHover: 'hover:bg-indigo-700',
    gradient: 'bg-gradient-to-r from-indigo-700 to-blue-600',
    light: 'bg-indigo-50',
    badge: 'bg-indigo-500',
    border: 'border-indigo-100',
    icon: '#4f46e5',
    accent: '#6366f1',
    primaryGradient: 'linear-gradient(to right, #4338ca, #2563eb)'
  }
};

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<ThemeColor>('rose');

  useEffect(() => {
    const savedTheme = localStorage.getItem('matrimony-theme') as ThemeColor;
    if (savedTheme && themes[savedTheme]) {
      setTheme(savedTheme);
    }
  }, []);

  const handleSetTheme = (newTheme: ThemeColor) => {
    setTheme(newTheme);
    localStorage.setItem('matrimony-theme', newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme: handleSetTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}